// The quest notice: a strip that says when a quest is taken, an objective done, a quest finished or failed, driven by
// @rpg/contracts' standard `story:quest` event. `noticeText` is the rule (pure); `mountQuestNotice` is the DOM strip
// (written straight to the element, no Preact render per frame, as the HUD's values; decision 0046), shown for a few
// seconds of the game's clock.
import { directText } from "@rpg/ui";
import type { JournalEntry } from "@rpg/story";
import type { StandardEvents } from "@rpg/contracts";
import { JOURNAL_CSS, JOURNAL_LABELS, type JournalLabels } from "./screen";

/** A quest change, as `story:quest` carries it. */
export type QuestChange = StandardEvents["story:quest"];

/** What a change says (null: nothing): a quest done or failed by its title; an active change naming an objective by
 *  the last objective done, or, with none done yet, the quest taken. An offer, and an active change with no current
 *  objective, say nothing. */
export function noticeText(change: QuestChange, o: { title: string | null; entry?: JournalEntry | undefined }, t: (id: string, vars?: Record<string, string | number>) => string, labels: JournalLabels = JOURNAL_LABELS): string | null {
  if (o.title === null) return null;
  const title = t(o.title);
  if (change.state === "offered" || (change.state === "active" && !change.objective)) return null;
  if (change.state === "done") return t(labels.noticeDone, { title });
  if (change.state === "failed") return t(labels.noticeFailed, { title });
  const done = o.entry?.objectives.filter((x) => x.done) ?? [];
  return done.length ? t(labels.noticeObjective, { label: t(done[done.length - 1]!.label) }) : t(labels.noticeQuest, { title });
}

/** Mount the notice strip (and the journal's styles, once). Feed it changes with `change`; call `update` each frame. */
export function mountQuestNotice(o: {
  /** A quest's title (a string id), or null when the source doesn't know it. */
  title(quest: string): string | null;
  /** Its journal entry now. */
  entry(quest: string): JournalEntry | undefined;
  t(id: string, vars?: Record<string, string | number>): string;
  /** The game's clock (seconds). */
  now(): number;
  /** Seconds a notice stays. Default 4. */
  seconds?: number;
  labels?: JournalLabels;
  doc?: Document;
}) {
  const doc = o.doc ?? document;
  if (!doc.getElementById("rpg-quest-journal-css")) {
    const s = doc.createElement("style");
    s.id = "rpg-quest-journal-css";
    s.textContent = JOURNAL_CSS;
    doc.head.appendChild(s);
  }
  const el = doc.createElement("div");
  el.className = "rpg-quest-notice";
  el.setAttribute("role", "status");
  doc.body.appendChild(el);
  const text = directText(el);
  let until = -1;
  return {
    /** A quest changed (a `story:quest` payload). */
    change(c: QuestChange): void {
      const s = noticeText(c, { title: o.title(c.quest), entry: o.entry(c.quest) }, o.t, o.labels);
      if (s === null) return;
      text.set(s);
      el.dataset.shown = "";
      until = o.now() + (o.seconds ?? 4);
    },
    /** Hide the notice once its time is up. */
    update(): void {
      if (until >= 0 && o.now() > until) {
        el.removeAttribute("data-shown");
        until = -1;
      }
    },
    /** What it says now (null when hidden). */
    text: (): string | null => (el.hasAttribute("data-shown") ? el.textContent : null),
  };
}
